/* eslint-disable prefer-const */
import { sanitizeUrl } from '@braintree/sanitize-url';
import moment from 'moment';
import 'moment-duration-format';
import { t } from './i18nService';
import objectTypes from '../constants/ObjectTypes';
import sessionReader from '../store/readHelpers/SessionReader';
import sessionActions from '../actions/SessionActions';
import storeManager from '../store/StoreManager';
import { reportError } from './ErrorReporting';

const utilService = {
    getCurrentDate() {
        return new Date();
    },

    getCurrentTimeStamp() {
        return this.getCurrentDate().getTime();
    },

    getIsoDateStamp(date = this.getCurrentDate()) {
        const fullString = date.toISOString();

        return `${fullString.slice(0, 19)}Z`;
    },

    getTimezoneOffsetString(date = new Date()) {
        const minutes = date.getTimezoneOffset() * -1;
        const hours = Math.floor(minutes / 60);
        const sign = minutes < 0 ? '-' : '+';
        const restMinutes = minutes % 60;

        return `${sign}${this.zeroPad(hours)}:${this.zeroPad(restMinutes)}`;
    },

    getIntOr(possibleInt, orValue) {
        const int = parseInt(possibleInt, 10);
        return isNaN(int) ? orValue : int;
    },

    /**
     * @param {string|boolean} value Boolean or 'true' or 'false' string
     */
    toBoolean(value) {
        if (typeof value === 'boolean') {
            return value;
        }

        return typeof value === 'string' && value.toLowerCase() === 'true';
    },

    toOptionalBoolean(value) {
        if (value === undefined) {
            return undefined;
        }

        return this.toBoolean(value);
    },

    /**
     * If utcMilli is provided, it returns the local date and time considering the timezone offset
     * @param utcMilli: UTC time in milliseconds
     * @returns {Date}
     */

    getLocalDateTimeFromUTC(utcMilli) {
        if (utcMilli) {
            const timeZoneOffsetMilli = new Date().getTimezoneOffset() * 60 * 1000;

            return new Date(utcMilli + timeZoneOffsetMilli);
        }

        return new Date();
    },

    getUrlParam(paramName, search = window.location.search) {
        const param = search
            .split(/\?|&/)
            .find(item => item.split('=')[0] === paramName);

        return param ? param.split('=')[1] : undefined;
    },

    addUrlParams(parameters, search = window.location.search, state = storeManager.getState()) {
        const params = new URLSearchParams(search);
        const { requestParameterIds } = state.session;
        Object.keys(parameters).forEach((paramName) => {
            params.set(paramName, parameters[paramName]);
        });
        storeManager.dispatch(
            sessionActions.setRequestParameterIds(
                Object.assign({}, requestParameterIds, parameters)
            )
        );
        window.history.replaceState({}, '', `${location.pathname}?${params}${location.hash}`);
    },

    removeUrlParams(...parameters) {
        const search = window.location.search;
        const state = storeManager.getState();
        const { requestParameterIds } = state.session;
        const requestParameterIdsClone = Object.assign({}, requestParameterIds);
        const params = new URLSearchParams(search);

        parameters.forEach((param) => {
            params.delete(param);
            delete requestParameterIdsClone[param];
        });

        storeManager.dispatch(
            sessionActions.setRequestParameterIds(requestParameterIdsClone)
        );
        window.history.replaceState({}, '', `${location.pathname}?${params}${location.hash}`);
    },

    /**
     * Stripping the host name from an url. https://www.nrk.no/urix becomes www.nrk.no
     * @param url
     * @returns {string}
     */
    getDomainFromUrl(url) {
        let hostname;
        if (url.indexOf('://') > -1) {
            hostname = url.split('/')[2];
        } else {
            hostname = url.split('/')[0];
        }
        // find and remove port number
        hostname = hostname.split(':')[0];
        // find and remove "?"
        hostname = hostname.split('?')[0];
        return hostname;
    },

    generateUrlForContentItemId(contentItemId, type, cacheable) {
        let url = `/file/ci/${contentItemId}/${type}`;
        const assessmentRunId = sessionReader.getAssessmentRunId();
        if (assessmentRunId) {
            url += `?assessmentRunId=${assessmentRunId}`;
        }
        if (cacheable) {
            url += `${url.includes('?') ? '&' : '?'}cacheable=true`;
        }
        return url;
    },

    isSameOrigin(url) {
        if (url.startsWith('/')) {
            return true;
        }
        const { host, protocol, port } = this.getUrlParts(url);
        return host === document.location.host && protocol === document.location.protocol && port === document.location.port;
    },

    getUrlParts(url) {
        const re = /((https?:)\/\/([.a-zA-Z0-9-]+)(:([0-9]+))?)?(\/(([.+a-zA-Z0-9_-]+\/?)?)+)?(\?[^#]+)?/;
        const matches = re.exec(url);

        return {
            url,
            protocol: matches[2],
            host: matches[3],
            port: matches[4] || '',
            pathname: matches[6] || '',
            search: matches[9],
        };
    },

    /**
     * isSameOriginMessageEvent is for message.origin, which is not a complete
     * URL.
     */
    isSameOriginMessageEvent(origin, { protocol, host } = window.location) {
        return origin === `${protocol}//${host}`;
    },

    /**
     * Creates an object like this {days, hours, minutes, seconds}
     *
     * @param {int} ms
     * @returns {{days, hours, minutes, seconds}}
     */
    getTimeUnitsFromDuration(ms) {
        let days;
        let hours;
        let minutes;
        let seconds;

        seconds = Math.floor(ms / 1000);
        minutes = Math.floor(seconds / 60);
        seconds %= 60;
        hours = Math.floor(minutes / 60);
        minutes %= 60;
        days = Math.floor(hours / 24);
        hours %= 24;

        return { days, hours, minutes, seconds };
    },

    getReadableDuration(milliseconds, showSeconds) {
        const isReducedRelativeTimeDimensionEnabled = sessionReader.isReducedRelativeTimeDimensionEnabled();
        const unitPartsTemplate = isReducedRelativeTimeDimensionEnabled ? 'W,D,H,m,s' : 'Y,M,W,D,H,m,s';
        // return [year, month, week, hour, minute, second]
        const timeUnitParts = moment.duration(milliseconds, 'milliseconds').format(unitPartsTemplate, { trim: false }).split(',');
        let index = 0;
        const year = isReducedRelativeTimeDimensionEnabled ? undefined : this.getFormattedTimeUnit(timeUnitParts[index++], 'year');
        const month = isReducedRelativeTimeDimensionEnabled ? undefined : this.getFormattedTimeUnit(timeUnitParts[index++], 'month');
        const week = this.getFormattedTimeUnit(timeUnitParts[index++], 'week');
        const day = this.getFormattedTimeUnit(timeUnitParts[index++], 'day');
        const hour = this.getFormattedTimeUnit(timeUnitParts[index++], 'hour');
        const minute = this.getFormattedTimeUnit(timeUnitParts[index++], 'minute');
        const second = this.getFormattedTimeUnit(timeUnitParts[index++], 'second');
        const formatUnit = (unit) => { return `${unit ? `${unit}, ` : ''}`; };

        let timeString = isReducedRelativeTimeDimensionEnabled ?
            `${formatUnit(week)}${formatUnit(day)}${formatUnit(hour)}` :
            `${formatUnit(year)}${formatUnit(month)}${formatUnit(week)}${formatUnit(day)}${formatUnit(hour)}`;
        if (showSeconds && second) {
            timeString += formatUnit(minute) + second;
        } else if (minute) {
            timeString += minute;
        } else {
            timeString = timeString.slice(0, -2);
        }

        this.checkIfTimeUnitsNegative(timeUnitParts, milliseconds, timeString);
        return timeString;
    },

    checkIfTimeUnitsNegative(timeUnitParts, milliseconds, timeString) {
        if (timeUnitParts.some(unit => unit < 0)) {
            reportError(`Negative remaining time unit detected: '${timeString}' (${milliseconds}ms)`);
        }
    },

    getFormattedTimeUnit(unitNumber, format) {
        return Number(unitNumber) !== 0 ?
            // captions
            `${unitNumber} ${t(`generic.timeUnit.${format}`, { count: Number(unitNumber) })}` :
            0;
    },

    getCandidateInfoString(userInfo, candidateId) {
        if (userInfo) {
            return `${candidateId || ''} ${userInfo.firstName || ''} ${userInfo.lastName || ''} ${userInfo.socialSecurityNumber || ''}`;
        } else if (candidateId) {
            return `${candidateId || ''}`;
        }

        return t('header.candidatePlaceholder');
    },

    zeroPad(number) {
        return (number < 10 ? '0' : '') + number;
    },

    /**
     * Converting number into letter.
     * 1 -> A, 26 -> Z
     * 27 -> AA, 43 - > AQ, etc
     * @param aNumber
     * @returns {string|*}
     */
    toLatinLetter(aNumber) {
        let number = parseInt(aNumber, 10);

        if (isNaN(number) || number <= 0) {
            return '';
        }

        let baseChar = ('A').charCodeAt(0);
        let letters = '';

        do {
            number -= 1;
            letters = String.fromCharCode(baseChar + (number % 26)) + letters;
            number = Math.floor(number / 26);
        } while (number > 0);

        return letters;
    },

    /**
     * https://stackoverflow.com/questions/9083037/convert-a-number-into-a-roman-numeral-in-javascript/9083076
     * @param {number} aNumber
     * @returns {string}
     */
    toRomanNumeral(aNumber) {
        const romanMatrix = [
            [1000, 'M'],
            [900, 'CM'],
            [500, 'D'],
            [400, 'CD'],
            [100, 'C'],
            [90, 'XC'],
            [50, 'L'],
            [40, 'XL'],
            [10, 'X'],
            [9, 'IX'],
            [5, 'V'],
            [4, 'IV'],
            [1, 'I'],
        ];
        const number = parseInt(aNumber, 10);
        let roman = '';

        if (!isNaN(number) && number > 0) {
            for (let i = 0; i < romanMatrix.length; i++) {
                if (number >= romanMatrix[i][0]) {
                    roman = (romanMatrix[i][1] + this.toRomanNumeral(number - romanMatrix[i][0])).toLowerCase();
                    break;
                }
            }
        }

        return roman;
    },

    /**
     * Handles 1 -> 20.
     * @param number
     * @returns {string|undefined}
     */
    getUpperCaseWordFromNumber(number) {
        if (number === 1) {
            return 'ONE';
        }
        if (number === 2) {
            return 'TWO';
        }
        if (number === 3) {
            return 'THREE';
        }
        if (number === 4) {
            return 'FOUR';
        }
        if (number === 5) {
            return 'FIVE';
        }
        if (number === 6) {
            return 'SIX';
        }
        if (number === 7) {
            return 'SEVEN';
        }
        if (number === 8) {
            return 'EIGHT';
        }
        if (number === 9) {
            return 'NINE';
        }
        if (number === 10) {
            return 'TEN';
        }
        if (number === 11) {
            return 'ELEVEN';
        }
        if (number === 12) {
            return 'TWELVE';
        }
        if (number === 13) {
            return 'THIRTEEN';
        }
        if (number === 14) {
            return 'FOURTEEN';
        }
        if (number === 15) {
            return 'FIFTEEN';
        }
        if (number === 16) {
            return 'SIXTEEN';
        }
        if (number === 17) {
            return 'SEVENTEEN';
        }
        if (number === 18) {
            return 'EIGHTEEN';
        }
        if (number === 19) {
            return 'NINETEEN';
        }
        if (number === 20) {
            return 'TWENTY';
        }

        return undefined;
    },

    // TODO: move to QuestionReader
    isQuestionTypeManuallyScored(questionType) {
        switch (questionType) {
        case objectTypes.CODE_EDITOR:
        case objectTypes.COMPILED_CODE_EDITOR:
        case objectTypes.EXTENDED_TEXT:
        case objectTypes.EXTENDED_TEXT_MATH:
        case objectTypes.TEXT_AREA:
        case objectTypes.CUSTOM_GEOGEBRA:
        case objectTypes.ORAL:
        case objectTypes.UPLOAD:
            return true;
        default:
            return false;
        }
    },

    isQuestionTypeNotScorable(questionType) {
        switch (questionType) {
        case objectTypes.DOCUMENT:
        case objectTypes.FORM:
            return true;
        default:
            return false;
        }
    },

    deepCloneObject(obj) {
        return JSON.parse(JSON.stringify(obj));
    },

    isObjectWithProps(thing) {
        return !!thing && typeof thing === 'object' && Object.keys(thing).length > 0;
    },

    /**
     * @param {string} str
     * @return {boolean}
     */
    isDataURL(str) {
        return !!str?.trim().startsWith('data:');
    },

    arrayFromObject(obj) {
        return Object.keys(obj).sort().map(key => obj[key]);
    },

    // source: https://vanillajstoolkit.com/polyfills/arrayflat/
    flatArray(array, depth = 1) {
        if (array.flat) {
            return array.flat(depth);
        }

        const flatten = (arr, flattenDepth) => {
            if (flattenDepth < 1) {
                return [...arr];
            }

            return arr.reduce((acc, val) =>
                acc.concat(Array.isArray(val) ? flatten(val, flattenDepth - 1) : val),
            []);
        };

        return flatten(array, depth);
    },

    callAfterAnimationFrames(func) {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                func();
            });
        });
    },

    /* intersperse: Return an array with the separator interspersed between
     * each element of the input array.
     *
     * [1,2,3].intersperse(0)
     * -> [1,0,2,0,3]
     */
    intersperse(originalArray, separator) {
        if (originalArray.length === 0) {
            return [];
        }

        return originalArray
            .slice(1)
            .reduce((newArray, item) => {
                return newArray.concat([separator, item]);
            }, [originalArray[0]]);
    },

    /**
     * Shuffles the items in the array in random order.
     *
     * @param {array} array
     * @returns {array} The same array that was passed in
     */
    shuffleArray: function shuffleArray(array) {
        let counter = array.length;

        // While there are elements in the array
        while (counter > 0) {
            // Pick a random index
            const index = Math.floor(Math.random() * counter);

            // Decrease counter by 1
            counter--;

            // And swap the last element with it
            const temp = array[counter];
            array[counter] = array[index];
            array[index] = temp;
        }

        return array;
    },

    insertInArray(value, position, array) {
        array.splice(position, 0, value);
        return array;
    },

    removeFromArray(position, array) {
        array.splice(position, 1);
        return array;
    },

    uniqueValuesArray(getValueToCompare, array) {
        const getValue = typeof getValueToCompare === 'function'
            ? getValueToCompare
            : item => item;

        return array.reduce((accumulator, outerItem) => {
            if (!accumulator.some(item => getValue(item) === getValue(outerItem))) {
                accumulator.push(outerItem);
            }
            return accumulator;
        }, []);
    },

    arraysHaveIdenticalValues(a1, a2) {
        if (a1.length === a2.length) {
            for (let i = 0; i < a1.length; i++) {
                if (a1[i] !== a2[i]) {
                    return false;
                }
            }
            return true;
        }
        return false;
    },

    shallowCompare(nextProps, current) {
        let allEqual = true;
        const keys = Object.keys(nextProps);
        keys.forEach((key) => {
            const equal = current[key] === nextProps[key];
            if (!equal) {
                allEqual = false;
            }
            console.log(`current.${key} === nextProps.${key}: ${equal ? 'TRUE' : 'FALSE'}`);
        });

        return allEqual;
    },

    isSeb() {
        const ua = window.navigator.userAgent;
        return ua.includes('SEB');
    },

    isIep() {
        const ua = window.navigator.userAgent;
        return ua.includes('IEP');
    },

    isSebForMac() {
        return this.isSeb() && this.isMac();
    },

    isSeb2ForWin() {
        const ua = window.navigator.userAgent;
        return ua.includes('Firefox/52.0');
    },

    isChrome() {
        const ua = window.navigator.userAgent;
        return ua.includes('Chrome');
    },

    isSafari() {
        const ua = window.navigator.userAgent;
        return ua.includes('Safari') && !this.isSeb() && !this.isChrome();
    },

    isIOS() {
        const { userAgent } = navigator;
        if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
            return true;
        }

        // This is actually the Safari browser on iPadOS.
        // If Apple ever releases a Mac with touch screen, we need to get even cleverer.
        return userAgent.includes('Macintosh') && utilService.isTouchDevice();
    },

    isMac() {
        const ua = window.navigator.userAgent;
        return ua.includes('Mac OS');
    },

    isLockedBrowser() {
        return this.isSeb() || this.isIep();
    },

    isPhantomJS() {
        const ua = window.navigator.userAgent;
        return ua.includes('PhantomJS');
    },

    isMobileDevice() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },

    isTouchDevice() {
        return 'ontouchstart' in document.documentElement;
    },

    isHeadless() {
        return !!navigator.webdriver;
    },

    toCamelCaseRemoveNonAlphaNumeric(stringInput) {
        if (!stringInput) {
            return undefined;
        }
        return `${stringInput}`
            .replace(/[^a-z0-9+]+/gi, ' ')
            .replace(new RegExp(/[-_]+/, 'g'), ' ')
            .replace(new RegExp(/[^\w\s]/, 'g'), '')
            .replace(
                new RegExp(/\s+(.)(\w+)/, 'g'),
                ($1, $2, $3) => `${$2.toUpperCase() + $3.toLowerCase()}`
            )
            .replace(new RegExp(/\s/, 'g'), '')
            .replace(new RegExp(/\w/), s => s.toLowerCase());
    },

    truncate(str, maxLength) {
        return str.length > maxLength ? `${str.substring(0, maxLength)}...` : str;
    },

    emptyObject(object) {
        Object.keys(object).forEach((key) => {
            delete object[key];
        });
    },

    /**
     *
     * @param {String|Number} number
     * @return {String|NaN} Parsed number or NaN if can't be parsed
     */
    parseFloatSafely(value) {
        if ([null, undefined].includes(value)) {
            return NaN;
        }

        const number = value.toString();
        const integerReg = /^-?\d+/;
        const fractionReg = /[.,](\d+)/;
        const originInteger = number.split(/[.,]/)[0];
        const integerMatch = number.match(integerReg);
        const fractionMatch = number.match(fractionReg);

        if (integerMatch === null) {
            return NaN;
        }

        const integerPart = integerMatch[0];
        if (integerPart.length < originInteger.length || fractionMatch === null) {
            return integerMatch[0];
        }

        return `${integerMatch[0]}.${fractionMatch[1]}`;
    },

    async clearClipboard() {
        try {
            await navigator.clipboard.writeText('');
        } catch (error) {
            this.clearClipboardFallback(error);
        }
    },

    clearClipboardFallback(writeTextError) {
        try {
            const hiddenStyle = `
position: absolute;
bottom: 0;
width: 1px;
height: 1px;
padding: 0;
margin: 0;
border: 0;
background-color: transparent;
color: transparent;`;

            const input = document.createElement('input');
            input.setAttribute('value', ' '); // space symbol is mandatory here
            input.setAttribute('style', hiddenStyle);
            input.setAttribute('aria-hidden', true);
            document.body.append(input);
            input.select();
            document.execCommand('copy');
            input.remove();
        } catch (error) {
            let errorMessage = 'Couldn\'t clear clipboard.';
            if (writeTextError) {
                errorMessage = `${errorMessage} 1 - ${writeTextError}; 2 - ${error}`;
            } else {
                errorMessage = `${errorMessage} ${error}`;
            }

            reportError(errorMessage);
        }
    },

    isBrowserStatusOnline() {
        // check strictly with false because in case if this property is not supported and equals undefined, this method should still return true
        return window.navigator.onLine !== false;
    },

    isObject(object, excludeArray = false) {
        return object !== null && (typeof object === 'object') && (!excludeArray || !Array.isArray(object));
    },

    isDeepEqual(value1, value2) {
        const isValueOneObject = this.isObject(value1);
        const isValueTwoObject = this.isObject(value2);

        if (isValueOneObject + isValueTwoObject === 1) {
            return false;
        }

        const areBothPlain = isValueOneObject + isValueTwoObject === 0;
        if (areBothPlain) {
            if (typeof (value1) === 'number' && isNaN(value1) && typeof (value2) === 'number' && isNaN(value2)) {
                return true;
            }

            return value1 === value2;
        }

        if (Array.isArray(value1) + Array.isArray(value2) === 1) {
            return false;
        }

        const objKeys1 = Object.keys(value1);
        const objKeys2 = Object.keys(value2);

        if (objKeys1.length !== objKeys2.length) {
            return false;
        }

        return objKeys1.every(key => this.isDeepEqual(value1[key], value2[key]));
    },

    isSafeUrl(url) {
        return sanitizeUrl(url) !== 'about:blank';
    },

    delay(ms) {
        if (!Number.isInteger(ms)) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            setTimeout(resolve, ms);
        });
    },

    arraysHaveSameValues(alphaArray, betaArray) {
        if (!Array.isArray(alphaArray) || !Array.isArray(betaArray)) {
            return false;
        }

        return alphaArray.some(alphaItem => betaArray.includes(alphaItem));
    },

    isDevelopmentQueryParameterTrue(parameter) {
        return process.env.NODE_ENV === 'development' && this.isQueryParameterTrue(parameter);
    },

    isQueryParameterTrue(parameter) {
        const queryParams = new URLSearchParams(window.location.search);
        return queryParams.get(parameter) === 'true';
    },
};

export default utilService;
